import React, { useEffect, useMemo, useState } from 'react';
import {
  Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, SubTabs, TextInput, fmtDate, panelPost,
  useAdminGet,
} from './kit';
import { Modal } from './email/emailKit';
import { ExcelButton } from './techpages/shared';
import type { AdminTargetIn } from './adminNav';
import AutoRules from './tags/AutoRules';
import OriginAdmin from './tags/OriginAdmin';
import {
  BulkTagBar, ColorPicker, EMPTY_TAG_FILTER, LIFECYCLES, LifecyclePill, RESERVED_TAG_KEYS, RowCheckbox,
  SelectionControls, TAGS_SECTION, TAG_COLORS, TagFilter, type TagFilterValue, TagPill, TagPills,
  describeTagFilter, invalidateTagOptions, saveTagDef, tagFilterFromParams, tagFilterParams, toLifecycle,
  useRowSelection, useTagOptions,
} from './tags/shared';

/**
 * User tags — one place to see every account's lifecycle and to assign the
 * admin's own tags in bulk (backend: Backend/user_tags.py, section "tags").
 *
 *  · Accounts  every account, server-paged, with the lifecycle / tag filters
 *              every other tab uses; tick rows or "select all N matching" and
 *              add or remove tags; paste an e-mail list to tag it; Excel.
 *  · Tags      the tag definitions: create, rename, recolour, group, delete.
 *  · Account origin  self sign-up vs added by admin: bulk-add batches to
 *              confirm, and origins set by hand (tags/OriginAdmin.tsx).
 *  · Overview  lifecycle counts (with the signed-in split) and tag counts;
 *              every number opens Accounts filtered to it.
 *
 * Lifecycle is computed live and cannot be assigned; tags are what the admin
 * sets. The filters here are the same params the e-mail targeting reads, so a
 * tag set here is immediately an audience in Email → Send to users.
 */

const TABS = ['Accounts', 'Tags', 'Automatic tags', 'Account origin', 'Overview'];
const TAB_BY_KEY: Record<string, string> = {
  accounts: 'Accounts', tags: 'Tags', auto: 'Automatic tags', automatic: 'Automatic tags', overview: 'Overview',
  origin: 'Account origin', bulk: 'Account origin',
};

/** Server-side export (admin-gated, audited): same params as tags/accounts. */
const EXPORT_PATH = '/api/v1/admin/tags/accounts.xlsx';

type Preset = { filter: TagFilterValue; nonce: number };

const UserTagsPanel: React.FC<{ target?: AdminTargetIn }> = ({ target }) => {
  const [tab, setTab] = useState(TABS[0]);
  const [preset, setPreset] = useState<Preset | null>(null);

  // Deep link: /admin?group=tags&tab=accounts&lifecycle=prospect&tags=webinar
  useEffect(() => {
    if (!target) return;
    const t = TAB_BY_KEY[String(target.tab || '').toLowerCase()];
    const f = tagFilterFromParams(target as Record<string, any>);
    if (t) setTab(t);
    if (!t || t === 'Accounts') setPreset({ filter: f, nonce: Date.now() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce]);

  const openAccounts = (patch: Partial<TagFilterValue>) => {
    setPreset({ filter: { ...EMPTY_TAG_FILTER, ...patch }, nonce: Date.now() });
    setTab('Accounts');
  };

  return (
    <div className="flex flex-col gap-4">
      <SubTabs tabs={TABS} active={tab} onChange={setTab} />
      {tab === 'Accounts' && <Accounts preset={preset} />}
      {tab === 'Tags' && <TagDefs onOpen={key => openAccounts({ tags: [key] })} />}
      {tab === 'Automatic tags' && <AutoRules onOpen={key => openAccounts({ tags: [key] })} />}
      {tab === 'Account origin' && <OriginAdmin />}
      {tab === 'Overview' && <Overview onOpen={openAccounts} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------
function useDebounced(value: string, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const parseEmails = (s: string) =>
  Array.from(new Set(s.split(/[\s,;]+/).map(x => x.trim().replace(/^<|>$/g, '').toLowerCase())
    .filter(x => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x))));

/** Header → the server's sort key; columns not listed do not sort. */
const COLS: { key: string; label: string; sort?: string }[] = [
  { key: 'lifecycle', label: 'Lifecycle', sort: 'lifecycle' },
  { key: 'tags', label: 'Tags', sort: 'tags' },
  { key: 'email', label: 'Email', sort: 'email' },
  { key: 'name', label: 'Name', sort: 'name' },
  { key: 'company', label: 'Company' },
  { key: 'country', label: 'Country' },
  { key: 'created_at', label: 'Created', sort: 'created' },
  // MAX(user_sessions.created_at): the last sign-in, not the last visit.
  { key: 'last_seen_at', label: 'Last sign-in', sort: 'last_seen' },
  { key: 'sessions', label: 'Sessions' },
];

const Accounts: React.FC<{ preset: Preset | null }> = ({ preset }) => {
  const opts = useTagOptions();
  const [qRaw, setQRaw] = useState('');
  const q = useDebounced(qRaw);
  const [tf, setTf] = useState<TagFilterValue>(preset?.filter || EMPTY_TAG_FILTER);
  const [country, setCountry] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [excludeTeam, setExcludeTeam] = useState(true);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'created', dir: 'desc' });
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('100');
  const [stamp, setStamp] = useState(0);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasted, setPasted] = useState('');
  const [err, setErr] = useState('');

  // A later preset (Overview / Tags click, deep link) replaces the tag filter.
  useEffect(() => {
    if (preset) { setTf(preset.filter); setQRaw(''); }
  }, [preset?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const countries = useAdminGet<string[]>('users', 'countries');

  /** Exactly the accounts params (no paging/sort) — also the bulk "filter" and the export. */
  const filterParams = useMemo(() => {
    const p: Record<string, string> = { ...tagFilterParams(tf), exclude_team: excludeTeam ? '1' : '0' };
    if (q.trim()) p.q = q.trim();
    if (country) p.country = country;
    if (createdFrom) p.created_from = createdFrom;
    if (createdTo) p.created_to = createdTo;
    return p;
  }, [tf, excludeTeam, q, country, createdFrom, createdTo]);
  const filterKey = JSON.stringify(filterParams);
  useEffect(() => { setPage(0); }, [filterKey, perPage, sort.key, sort.dir]);

  const list = useAdminGet<any>(TAGS_SECTION, 'accounts',
    { ...filterParams, sort: sort.key, dir: sort.dir, page, per_page: perPage },
    [filterKey, sort.key, sort.dir, page, perPage, stamp]);
  const rows: any[] = list.data?.rows || [];
  const total = Number(list.data?.total ?? 0);
  const pages = Math.max(1, Number(list.data?.pages ?? 1));
  const byLc: Record<string, number> = list.data?.counts?.by_lifecycle || {};

  const sel = useRowSelection({ idKey: 'user_id', resetKey: filterKey });

  const summary = useMemo(() => {
    const out = describeTagFilter(tf, k => opts.tagByKey[k]?.label || k);
    if (q.trim()) out.push(`Email / name / company contains “${q.trim()}”`);
    if (country) out.push(`Country: ${country}`);
    if (createdFrom || createdTo) out.push(`Created: ${createdFrom || '…'} → ${createdTo || '…'}`);
    out.push(excludeTeam ? 'Team left out' : 'Team included');
    return out;
  }, [tf, q, country, createdFrom, createdTo, excludeTeam, opts.tagByKey]);

  const clickSort = (key?: string) => {
    if (!key) return;
    setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'email' || key === 'name' || key === 'lifecycle' ? 'asc' : 'desc' });
  };
  const toggleLc = (k: string) =>
    setTf(f => ({ ...f, lifecycle: f.lifecycle.length === 1 && f.lifecycle[0] === k ? [] : [k] }));

  const emails = useMemo(() => parseEmails(pasted), [pasted]);
  const reset = () => {
    setTf(EMPTY_TAG_FILTER); setQRaw(''); setCountry(''); setCreatedFrom(''); setCreatedTo('');
    setExcludeTeam(true);
  };
  const reload = () => setStamp(s => s + 1);

  return (
    <div className="flex flex-col gap-4">
      <Section title="Find accounts"
        right={<div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-grayscale-1100 flex items-center gap-1.5 cursor-pointer"
            title="The analytics exclusion list, billing admins and @universitytech.io addresses">
            <input type="checkbox" checked={excludeTeam} onChange={e => setExcludeTeam(e.target.checked)} />
            Leave our team out
          </label>
          <Btn small onClick={reset}>Reset filters</Btn>
          <ExcelButton path={EXPORT_PATH} params={{ ...filterParams, sort: sort.key, dir: sort.dir }}
            fallback="user-tags-accounts" what="Every matching account" onError={setErr} />
        </div>}>
        <div className="flex items-end gap-3 flex-wrap">
          <label className="flex flex-col gap-1 text-xs text-grayscale-1100 flex-1 min-w-[16rem]">
            Search
            <input value={qRaw} onChange={e => setQRaw(e.target.value)} placeholder="Email, name or company…"
              className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300 w-full" />
          </label>
          <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
            Country
            <Select value={country} onChange={setCountry}
              options={['', ...((countries.data as any) || [])]} labels={{ '': 'Any country' }} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
            Created from
            <TextInput type="date" value={createdFrom} onChange={setCreatedFrom} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
            to
            <TextInput type="date" value={createdTo} onChange={setCreatedTo} />
          </label>
        </div>
        <TagFilter value={tf} onChange={v => setTf(v)} />
        {Object.keys(byLc).length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap text-xs text-grayscale-1100">
            <span>In this result:</span>
            {LIFECYCLES.filter(l => Number(byLc[l.key] || 0) > 0 || tf.lifecycle.includes(l.key)).map(l => (
              <button key={l.key} type="button" onClick={() => toggleLc(l.key)}
                title={tf.lifecycle.length === 1 && tf.lifecycle[0] === l.key ? 'Filtering by this — click to clear' : `Only ${l.label}`}
                className={`inline-flex items-center gap-1 rounded-full px-1 py-0.5 hover:bg-grayscale-200 ${
                  tf.lifecycle.length === 1 && tf.lifecycle[0] === l.key ? 'ring-1 ring-primary-500' : ''}`}>
                <LifecyclePill value={l.key} />
                <span className="text-grayscale-1300 font-medium">{Number(byLc[l.key] || 0).toLocaleString()}</span>
              </button>
            ))}
          </div>
        )}
        {err && <Msg kind="error">{err}</Msg>}
      </Section>

      <BulkTagBar
        title="Bulk tags"
        userIds={sel.allMatching ? [] : sel.ids}
        filter={sel.allMatching ? { params: filterParams, count: total, summary } : null}
        onClear={sel.count || sel.allMatching ? sel.clear : undefined}
        onDone={() => reload()} />

      <Section title={`Accounts (${total.toLocaleString()})`}
        right={<div className="flex items-center gap-2 flex-wrap">
          <SelectionControls sel={sel} rows={rows} total={total} />
          <Btn small onClick={reload}>Refresh</Btn>
        </div>}>
        <div className="text-xs text-grayscale-1100 -mt-1">{summary.join(' · ')}</div>
        {list.error && <Msg kind="error">{list.error}</Msg>}
        {list.loading && !list.data ? <Loading /> : (
          <div className={`overflow-auto rounded-lg border border-grayscale-300 max-h-[36rem] ${list.loading ? 'opacity-60' : ''}`}>
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-grayscale-200 z-10">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <input type="checkbox" aria-label="Select this page" className="cursor-pointer accent-primary-500"
                      checked={sel.allMatching || sel.allOnPage(rows)} disabled={!rows.length || sel.allMatching}
                      onChange={() => sel.togglePage(rows)} />
                  </th>
                  {COLS.map(c => (
                    <th key={c.key} onClick={() => clickSort(c.sort)}
                      title={c.sort ? 'Click to sort' : undefined}
                      className={`text-left font-grotesk text-xs px-3 py-2 whitespace-nowrap ${
                        c.sort ? 'cursor-pointer select-none hover:text-grayscale-1300' : ''} ${
                        sort.key === c.sort ? 'text-grayscale-1300 font-bold' : 'text-grayscale-1200'}`}>
                      {c.label}
                      {sort.key === c.sort && <span className="ml-1 text-[10px] opacity-70">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.user_id} className={`border-t border-grayscale-200 hover:bg-grayscale-100 ${
                    sel.isPicked(r) ? 'bg-primary-100/40' : ''}`}>
                    <td className="px-3 py-1.5"><RowCheckbox sel={sel} row={r} /></td>
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      <LifecyclePill value={r.lifecycle} subtag={r.subtag} label={r.lifecycle_label} />
                      {r.invite_sent === true && toLifecycle(r.lifecycle) !== 'prospect' && (
                        <span className="ml-1 text-[10px] text-grayscale-1100" title="An invite record exists">invited</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 min-w-[8rem]"><TagPills tags={r.tags} max={4} nowrap /></td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-grayscale-1300" title={r.user_id}>{r.email || '—'}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap max-w-[14rem] truncate" title={r.name || ''}>{r.name || '—'}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap max-w-[14rem] truncate" title={r.company || ''}>{r.company || '—'}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{r.country || '—'}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{fmtDate(r.last_seen_at)}</td>
                    <td className="px-3 py-1.5 text-right">{Number(r.sessions || 0).toLocaleString()}</td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr><td colSpan={COLS.length + 1} className="p-6 text-center text-sm text-grayscale-1100">
                    No account matches these filters.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-between gap-3 flex-wrap text-xs text-grayscale-1100">
          <span>
            Page {page + 1} of {pages} · showing {rows.length ? page * Number(perPage) + 1 : 0}–
            {page * Number(perPage) + rows.length} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={perPage} onChange={setPerPage} options={['50', '100', '250', '500']}
              labels={{ '50': '50 / page', '100': '100 / page', '250': '250 / page', '500': '500 / page' }} />
            <Btn small onClick={() => setPage(0)} disabled={page === 0}>« First</Btn>
            <Btn small onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>‹ Prev</Btn>
            <Btn small onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}>Next ›</Btn>
            <Btn small onClick={() => setPage(pages - 1)} disabled={page >= pages - 1}>Last »</Btn>
          </div>
        </div>
      </Section>

      <Section title="Tag a pasted list"
        right={<Btn small onClick={() => setPasteOpen(o => !o)}>{pasteOpen ? 'Hide' : 'Paste e-mails'}</Btn>}>
        {!pasteOpen ? (
          <div className="text-xs text-grayscale-1100 -mt-1">
            Paste addresses from a sheet or an event list and tag every one that is an account; the rest are listed.
          </div>
        ) : (
          <>
            <textarea value={pasted} rows={5} onChange={e => setPasted(e.target.value)}
              placeholder={'one@company.com\ntwo@company.com — commas, semicolons or new lines'}
              className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300 font-mono" />
            <div className="text-xs text-grayscale-1100">
              {emails.length.toLocaleString()} address(es) recognised
              {pasted.trim() && <> · <button type="button" className="underline" onClick={() => setPasted('')}>clear</button></>}
            </div>
            <BulkTagBar title="Tags for the pasted list" emails={emails} onDone={() => reload()} />
          </>
        )}
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tags — the definitions
// ---------------------------------------------------------------------------
type DefForm = { key: string; isNew: boolean; label: string; color: string; grp: string; description: string };

/** user_tags._slug, so the preview shows the key the server will derive. */
const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40).replace(/^-+|-+$/g, '');

const TagDefs: React.FC<{ onOpen: (key: string) => void }> = ({ onOpen }) => {
  const [stamp, setStamp] = useState(0);
  const defs = useAdminGet<any>(TAGS_SECTION, 'defs', {}, [stamp]);
  const rows: any[] = useMemo(() => [...(defs.data?.rows || [])].sort((a, b) =>
    String(a.grp || '~').localeCompare(String(b.grp || '~')) || String(a.label).localeCompare(String(b.label))),
  [defs.data]);
  const [form, setForm] = useState<DefForm | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [del, setDel] = useState<any | null>(null);
  const groups = useMemo(() => Array.from(new Set(rows.map(r => String(r.grp || '')).filter(Boolean))).sort(), [rows]);

  const blank = (): DefForm => ({ key: '', isNew: true, label: '', color: TAG_COLORS[rows.length % TAG_COLORS.length],
    grp: '', description: '' });
  const keyFor = (f: DefForm) => (f.isNew ? (f.key.trim() || slugify(f.label)) : f.key);
  const reserved = form ? RESERVED_TAG_KEYS.includes(keyFor(form)) : false;
  const badKey = form ? (form.isNew && !!form.key.trim() && !/^[a-z0-9-]{1,40}$/.test(form.key.trim())) : false;
  const clash = form?.isNew ? rows.some(r => r.key === keyFor(form)) : false;

  const save = async () => {
    if (!form || !form.label.trim()) return;
    setBusy(true); setMsg(null);
    const body: any = { label: form.label.trim().slice(0, 60), color: form.color,
      description: form.description.trim().slice(0, 300), grp: form.grp.trim().slice(0, 40) };
    if (!form.isNew) body.key = form.key;
    else if (form.key.trim()) body.key = form.key.trim();
    const { row, error } = await saveTagDef(body);
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setMsg({ kind: 'ok', text: `${form.isNew ? 'Created' : 'Saved'} “${row?.label || form.label}” (${row?.key || keyFor(form)}).` });
    setForm(null);
    setStamp(s => s + 1);
  };

  const remove = async () => {
    if (!del) return;
    const target = del;
    setDel(null); setBusy(true); setMsg(null);
    const { data, error } = await panelPost(TAGS_SECTION, 'def_delete', { key: target.key });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    invalidateTagOptions();
    setMsg({ kind: 'ok', text: `Deleted “${target.label}” and removed it from ${Number(data?.unassigned || 0).toLocaleString()} account(s).` });
    if (form && form.key === target.key) setForm(null);
    setStamp(s => s + 1);
  };

  return (
    <div className="flex flex-col gap-4">
      <Section title={form ? (form.isNew ? 'New tag' : `Edit “${form.label || form.key}”`) : 'Tags'}
        right={!form && <Btn small kind="primary" onClick={() => { setMsg(null); setForm(blank()); }}>+ New tag</Btn>}>
        {!form ? (
          <p className="text-xs text-grayscale-1100 -mt-1">
            Your own labels for accounts — a campaign, an event, a segment. Lifecycle (explorer, voyager paid /
            assigned / trial / trial lapsed, prospect) is computed from billing and sign-ins and is not a tag.
            Tags are assigned in Accounts, Users and User 360, and filter every people list in the admin and
            the audience in Email → Send to users.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex items-end gap-3 flex-wrap">
              <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
                Label
                <input value={form.label} maxLength={60} autoFocus placeholder="e.g. Webinar Oct 2026"
                  onChange={e => setForm({ ...form, label: e.target.value })}
                  className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-64" />
              </label>
              <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
                Key {form.isNew ? '(optional)' : '(fixed)'}
                <input value={form.isNew ? form.key : form.key} disabled={!form.isNew} maxLength={40}
                  placeholder={slugify(form.label) || 'derived from the label'}
                  onChange={e => setForm({ ...form, key: e.target.value.toLowerCase() })}
                  className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-52 font-mono disabled:bg-grayscale-200" />
              </label>
              <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
                Group (optional)
                <input value={form.grp} maxLength={40} list="tag-groups" placeholder="e.g. Campaigns"
                  onChange={e => setForm({ ...form, grp: e.target.value })}
                  className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-44" />
                <datalist id="tag-groups">{groups.map(g => <option key={g} value={g} />)}</datalist>
              </label>
              <div className="flex flex-col gap-1 text-xs text-grayscale-1100">
                Colour
                <ColorPicker value={form.color} onChange={c => setForm({ ...form, color: c })} />
              </div>
            </div>
            <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
              Description (optional)
              <textarea value={form.description} maxLength={300} rows={2}
                placeholder="What the tag means, so the next admin applies it the same way"
                onChange={e => setForm({ ...form, description: e.target.value })}
                className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300" />
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-grayscale-1100">Preview</span>
              <TagPill tag={{ key: keyFor(form), label: form.label.trim() || 'New tag', color: form.color }} />
              <span className="text-[11px] text-grayscale-1100 font-mono">{keyFor(form) || '—'}</span>
            </div>
            {reserved && <Msg kind="error">“{keyFor(form)}” is reserved for the lifecycle — pick another key or label.</Msg>}
            {badKey && <Msg kind="error">A key is lowercase letters, digits and dashes, at most 40 characters.</Msg>}
            {clash && <Msg kind="error">A tag with the key “{keyFor(form)}” already exists — edit that one instead.</Msg>}
            <div className="flex gap-2">
              <Btn kind="primary" onClick={save} disabled={busy || !form.label.trim() || reserved || badKey || clash}>
                {busy ? 'Saving…' : form.isNew ? 'Create tag' : 'Save changes'}
              </Btn>
              <Btn onClick={() => setForm(null)}>Cancel</Btn>
            </div>
          </div>
        )}
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      </Section>

      <Section title={`Defined tags (${rows.length})`} right={<Btn small onClick={() => setStamp(s => s + 1)}>Refresh</Btn>}>
        {defs.error && <Msg kind="error">{defs.error}</Msg>}
        {defs.loading && !defs.data ? <Loading /> : (
          <DataTable rows={rows} csvName="user_tag_defs"
            columns={['label', 'key', 'grp', 'description', 'n', 'created_by', 'created_at']}
            labels={{ label: 'Tag', grp: 'Group', n: 'Accounts', created_by: 'Created by', created_at: 'Created' }}
            empty="No tags yet — create the first one above."
            render={{
              label: r => <TagPill tag={{ key: r.key, label: r.label, color: r.color }} />,
              key: r => <span className="font-mono text-xs">{r.key}</span>,
              n: r => (
                <button type="button" onClick={() => onOpen(r.key)} title="Open Accounts filtered to this tag"
                  className="text-primary-700 underline underline-offset-2">
                  {Number(r.n || 0).toLocaleString()}
                </button>
              ),
            }}
            actions={r => (
              <div className="flex gap-1">
                <Btn small onClick={() => { setMsg(null); setForm({ key: r.key, isNew: false, label: r.label || '',
                  color: r.color || TAG_COLORS[0], grp: r.grp || '', description: r.description || '' }); }}>Edit</Btn>
                <Btn small kind="danger" onClick={() => setDel(r)}>Delete</Btn>
              </div>
            )} />
        )}
      </Section>

      {del && (
        <Modal title={`Delete the tag “${del.label}”?`} onClose={() => setDel(null)}
          footer={<>
            <Btn onClick={() => setDel(null)}>Cancel</Btn>
            <Btn kind="danger" onClick={remove}>
              Delete{Number(del.n || 0) ? ` and untag ${Number(del.n).toLocaleString()}` : ''}
            </Btn>
          </>}>
          <TagPill tag={{ key: del.key, label: del.label, color: del.color }} />
          <p>
            The definition goes, and so does every assignment of it —
            {' '}<b>{Number(del.n || 0).toLocaleString()} account(s)</b> lose the tag. There is no undo;
            the audit log keeps the record.
          </p>
        </Modal>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------
const Overview: React.FC<{ onOpen: (f: Partial<TagFilterValue>) => void }> = ({ onOpen }) => {
  const ov = useAdminGet<any>(TAGS_SECTION, 'overview');
  const d = ov.data || {};
  const lc: any[] = d.lifecycle || [];
  const tags: any[] = d.tags || [];
  const totals = d.totals || {};
  const lcRow = (k: string) => lc.find(x => toLifecycle(x?.key) === k) || {};
  const grouped = useMemo(() => {
    const m = new Map<string, any[]>();
    [...tags].sort((a, b) => Number(b.n || 0) - Number(a.n || 0)).forEach(t => {
      const g = String(t.grp || '');
      if (!m.has(g)) m.set(g, []);
      m.get(g)!.push(t);
    });
    return Array.from(m.entries()).sort(([a], [b]) => (a ? a : '~').localeCompare(b ? b : '~'));
  }, [tags]);

  return (
    <div className="flex flex-col gap-4">
      {ov.error && <Msg kind="error">{ov.error}</Msg>}
      {ov.loading && !ov.data ? <Loading /> : (
        <>
          <KPIRow>
            <KPI label="Accounts" value={Number(totals.accounts ?? 0).toLocaleString()} hint="Every account, team left out"
              onClick={() => onOpen({})} />
            <KPI label="Signed in" value={Number(totals.signed_in ?? 0).toLocaleString()}
              hint="At least once — OTP, Google or LinkedIn" onClick={() => onOpen({ signedIn: '1' })} />
            <KPI label="Prospects" value={Number(totals.prospects ?? 0).toLocaleString()}
              hint="Never signed in" onClick={() => onOpen({ lifecycle: ['prospect'] })} />
            <KPI label="Tagged" value={Number(totals.tagged ?? 0).toLocaleString()} hint="Carry at least one custom tag" />
          </KPIRow>
          {d.team_excluded_count !== undefined && d.team_excluded_count !== null && (
            <div className="text-xs text-grayscale-1100 -mt-2">
              {Number(d.team_excluded_count).toLocaleString()} team account(s) — the analytics exclusion list,
              billing admins and @universitytech.io — are left out of these counts.
            </div>
          )}

          <Section title="Lifecycle" right={<Btn small onClick={ov.reload}>Refresh</Btn>}>
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {LIFECYCLES.map(l => {
                const r = lcRow(l.key);
                const n = Number(r.n || 0);
                const si = r.signed_in_n;
                return (
                  <button key={l.key} type="button" onClick={() => onOpen({ lifecycle: [l.key] })}
                    title={`Open Accounts filtered to ${l.label}`}
                    className="text-left rounded-xl border border-grayscale-300 bg-white px-4 py-3 hover:border-primary-400 flex flex-col items-start gap-1">
                    <LifecyclePill value={l.key} />
                    <span className="text-xl font-bold text-grayscale-1300 font-grotesk">{n.toLocaleString()}</span>
                    <span className="text-[11px] text-grayscale-1100">
                      {si !== undefined && si !== null
                        ? `${Number(si).toLocaleString()} signed in · ${(n - Number(si)).toLocaleString()} never`
                        : l.hint}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-grayscale-1100">
              Trial, trial lapsed, paid and assigned come from billing whatever the sign-in history; an explorer
              who has never signed in is a prospect (every such account was created by an import or an invite).
            </p>
          </Section>

          <Section title={`Tags (${tags.length})`}>
            {!tags.length ? (
              <div className="text-sm text-grayscale-1100">No tags defined yet — create them on the Tags tab.</div>
            ) : grouped.map(([g, list]) => (
              <div key={g || '_'} className="flex flex-col gap-1.5">
                <div className="text-[11px] uppercase tracking-wide text-grayscale-1100">{g || 'Ungrouped'}</div>
                <div className="flex flex-wrap gap-2">
                  {list.map(t => (
                    <button key={t.key} type="button" onClick={() => onOpen({ tags: [t.key] })}
                      title={t.description || `Open Accounts filtered to ${t.label}`}
                      className="inline-flex items-center gap-1.5 rounded-full px-1 py-0.5 hover:bg-grayscale-200">
                      <TagPill tag={{ key: t.key, label: t.label, color: t.color }} />
                      <span className="text-sm font-medium text-grayscale-1300">{Number(t.n || 0).toLocaleString()}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </Section>
        </>
      )}
    </div>
  );
};

export default UserTagsPanel;
